import { memo, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { useGetEventRecordingTranscribesQuery } from '@crew/apis/eventRecordingTranscribe/eventRecordingTranscribeApis'

import { useAppSelector } from 'states/hooks'

import { EventDetailMeetingMinutesPanelTranscriptionListItem } from './components/eventDetailMeetingMinutesPanelTranscriptionListItem/eventDetailMeetingMinutesPanelTranscriptionListItem'
import { useEffectOnce } from '@dx-system/react-use'

export const EventDetailMeetingMinutesPanelTranscriptionList = memo(() => {
  const { eventId } = useParams()

  const eventEventRecordingTranscribeMessage = useAppSelector(
    (state) => state.app.eventRecordingTranscribeEventMessage
  )

  // この処理が流れる際、eventIdが存在しない場合は正しく描画できないはずなのでエラーを投げる
  // （以降の処理の型制約上もこちらの方が都合が良いはず）
  if (!eventId) {
    throw new Error('eventId is undefined')
  }

  // 文字起こしリスト取得API
  const {
    data: getEventRecordingTranscribesResult,
    refetch: getEventRecordingTranscribesrefetch,
  } = useGetEventRecordingTranscribesQuery({
    eventId: eventId,
  })

  // 文字起こしリスト再取得
  useEffect(() => {
    if (eventEventRecordingTranscribeMessage) {
      getEventRecordingTranscribesrefetch()
    }
  }, [
    eventEventRecordingTranscribeMessage,
    getEventRecordingTranscribesrefetch,
  ])

  // スクロール対象の文字起こしIdを取得
  const scrollToTranscribeId = useAppSelector(
    (state) => state.eventDetailPage.scrollToTranscribeId
  )

  // 文字起こし表示領域のref
  const itemsScrollableDivRef = useRef<HTMLDivElement>(null)

  // 指定した文字起こしを表示領域内にスクロールする
  const scrollToTranscribe = useCallback(
    (transcribeId: string | undefined) => {
      // 文字起こしIdが指定されていない場合、処理しない
      if (transcribeId === undefined) {
        return
      }

      // スクロール対象のDOM要素
      const displayTargetElement = itemsScrollableDivRef.current?.querySelector(
        // 文字起こしIDが数字始まりの場合、CSSセレクタとしては無効になってしまいエラーが発生するため、
        // CSS.escapeを使用して文字起こしIDを安全にCSSセレクタとして使用する
        `#${CSS.escape(transcribeId)}`
      )

      // 文字起こし一覧
      const transcribes =
        getEventRecordingTranscribesResult?.eventRecordings?.flatMap(
          (recording) => recording.eventRecordingTranscribes
        ) ?? []

      // スクロール対象の文字起こしを文字起こし一覧の中から取得する
      const item = transcribes.find(
        (transcribe) => transcribe.id === transcribeId
      )

      // 「スクロール対象のメッセージが表示アイテムの中にある」かつ「スクロール対象のDOM要素がある」場合のみスクロール処理を行う
      if (item && displayTargetElement) {
        // 表示対象のDOM要素を表示領域内にスクロールする
        displayTargetElement.scrollIntoView({
          behavior: 'auto',
          // 議事録タブ<->文字起こしタブの切替を複数回行った際にスクロール位置がずれないよう、
          // 要素の下端を表示領域の下端に合わせる
          block: 'end',
        })
      }
    },
    [getEventRecordingTranscribesResult?.eventRecordings]
  )

  // コンポーネントがマウントされた際に、指定された文字起こしIdがある場合はスクロールする
  useEffectOnce(() => {
    if (scrollToTranscribeId) {
      // スクロール対象の文字起こしを表示領域内にスクロールする
      scrollToTranscribe(scrollToTranscribeId)
    }
  })

  // 文字起こしリストが取得できない場合は何も表示しない
  if (
    !getEventRecordingTranscribesResult ||
    getEventRecordingTranscribesResult.eventRecordings.length === 0
  ) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-3" ref={itemsScrollableDivRef}>
      {getEventRecordingTranscribesResult.eventRecordings.map(
        (eventRecording) => (
          <EventDetailMeetingMinutesPanelTranscriptionListItem
            key={eventRecording.id}
            eventRecording={eventRecording}
          />
        )
      )}
    </div>
  )
})
